import { useEffect, useState } from 'react';
import { useTranslation } from './hooks/useTranslation';
import { useButtons, useAuth } from './platform';
import { Plus, RefreshCw, ChevronRight, Building2, ChevronDown, Clock, CheckCircle, XCircle, AlertCircle, LogIn } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Badge, Input } from './components/ui';
import { Spinner } from './components/ui/spinner';
import { cn } from './lib/utils';

// Status display configuration
const STATUS_CONFIG = {
  pending: { icon: Clock, label: 'Under Review', variant: 'warning' },
  approved: { icon: AlertCircle, label: 'Awaiting Join', variant: 'warning' },
  success: { icon: CheckCircle, label: 'Active', variant: 'success' },
  rejected: { icon: XCircle, label: 'Rejected', variant: 'destructive' },
};

function AccountsPage({ setAppLocation }) {
  const { t } = useTranslation();
  const { hideAllButtons } = useButtons();
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createFormExpanded, setCreateFormExpanded] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    full_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    address: '',
    legal_name: '',
    company_about: '',
    advertising_about: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    hideAllButtons();
  }, [hideAllButtons]);

  // Fetch accounts data on mount and poll every 10 seconds
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    fetchAccountsData();

    // Poll every 10 seconds while on this page
    const pollInterval = setInterval(() => {
      fetchAccountsDataSilent();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [isAuthenticated]);

  const fetchAccountsData = () => {
    setLoading(true);
    setError(null);

    fetch('/api/accounts', {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load accounts');
        return res.json();
      })
      .then(data => {
        setAccounts(data.accounts || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Accounts fetch error:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  // Silent fetch for polling - no loading state changes
  const fetchAccountsDataSilent = () => {
    fetch('/api/accounts', {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (!res.ok) return;
        return res.json();
      })
      .then(data => {
        if (data) {
          setAccounts(data.accounts || []);
        }
      })
      .catch(() => {
        // Silently ignore errors during polling
      });
  };

  const handleAccountSelect = (account) => {
    if (account.status !== 'approved' && account.status !== 'success') return;
    setAppLocation(`/acc-${account.id}/ads-balance`);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!formData.title || !formData.full_name || !formData.email || !formData.phone || !formData.country || !formData.city || !formData.address || !formData.company_about || !formData.advertising_about) {
      alert(t('all_fields_required') || 'All fields are required');
      return;
    }

    // Validate character limits for about fields
    if (formData.company_about.length < 10 || formData.company_about.length > 200) {
      alert(t('company_about_length') || 'Company description must be between 10 and 200 characters');
      return;
    }
    if (formData.advertising_about.length < 10 || formData.advertising_about.length > 200) {
      alert(t('advertising_about_length') || 'Advertising description must be between 10 and 200 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/accounts', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create account');
      }

      const data = await response.json();

      // Add new account to list
      if (data.account) {
        setAccounts(prev => [data.account, ...prev]);
      }

      // Reset form
      setFormData({
        title: '',
        full_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        legal_name: '',
        address: '',
        company_about: '',
        advertising_about: ''
      });
      setCreateFormExpanded(false);

      // Show success message (optional)
      // alert(t('account_created') || 'Account submitted for review');
    } catch (err) {
      console.error('Create account error:', err);
      alert(t('account_creation_failed') || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-wrap pb-6">
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('accounts') || 'Ad Accounts'}</CardTitle>
            <CardDescription>
              {t('login_required') || 'Please log in to access your accounts.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-wrap">
        <div className="flex items-center justify-center py-20">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrap pb-6">
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('accounts') || 'Ad Accounts'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-destructive mb-4">{error}</p>
              <Button variant="outline" onClick={fetchAccountsData}>
                <RefreshCw size={16} />
                <span>{t('try_again') || 'Try Again'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-wrap pb-6">
      {/* Header */}
      <div className="text-center pt-8 pb-6">
        <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5 rounded-2xl mb-4">
          <Building2 size={32} className="text-primary" />
        </div>
        <h1 className="text-2xl font-bold mb-2">{t('select_account') || 'Select an Account'}</h1>
        <p className="text-muted-foreground">
          {t('select_account_subtitle') || 'Choose an account to manage its Telegram Ads balance'}
        </p>
      </div>

      {/* Accounts List */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('your_accounts') || 'Your Accounts'}</CardTitle>
            <Button variant="ghost" size="icon" onClick={fetchAccountsData} title="Refresh">
              <RefreshCw size={18} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {accounts.length > 0 ? (
            <div className="space-y-2">
              {accounts.map(account => {
                const status = STATUS_CONFIG[account.status] || STATUS_CONFIG.pending;
                const StatusIcon = status.icon;
                const isSuccess = account.status === 'success';
                const isApproved = account.status === 'approved';
                const isClickable = isSuccess || isApproved;

                const handleClick = () => {
                  if (isSuccess) {
                    // Go to balance page for fully set up accounts
                    handleAccountSelect(account);
                  } else if (isApproved && account.join_link) {
                    // Open join link in new window for accounts awaiting join
                    window.open(account.join_link, '_blank', 'noopener,noreferrer');
                  }
                };

                return (
                  <div
                    key={account.id}
                    className={cn(
                      "flex items-center gap-3 w-full p-3 rounded-lg transition-colors text-left",
                      isClickable
                        ? "bg-secondary/50 hover:bg-secondary cursor-pointer"
                        : "bg-secondary/30"
                    )}
                    onClick={handleClick}
                    role={isClickable ? "button" : undefined}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{account.title || 'Untitled Account'}</div>
                      <div className="text-sm text-muted-foreground truncate">{account.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{account.country}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {isSuccess ? (
                        <Button size="sm" variant="default" className="h-7 text-xs">
                          {t('open') || 'Open'}
                          <ChevronRight size={14} className="ml-1" />
                        </Button>
                      ) : isApproved && account.join_link ? (
                        <Button size="sm" variant="warning" className="h-7 text-xs">
                          {t('join_chat') || 'Join Chat'}
                          <LogIn size={14} className="ml-1" />
                        </Button>
                      ) : (
                        <>
                          <Badge variant={status.variant} className="gap-1">
                            <StatusIcon size={12} />
                            <span>{t(`status_${account.status}`) || status.label}</span>
                          </Badge>
                          {account.status === 'rejected' && account.reject_reason && (
                            <p className="text-xs text-destructive max-w-[150px] text-right">
                              {account.reject_reason}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📭</div>
              <p className="font-medium mb-1">{t('no_accounts') || 'No ad accounts yet'}</p>
              <p className="text-sm text-muted-foreground">
                {t('create_account_hint') || 'Create an account to start advertising on Telegram'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Account Section */}
      <Card>
        {accounts.length > 0 ? (
          // Collapsible header when user has accounts
          <button
            className="w-full flex items-center justify-between p-4 text-left"
            onClick={() => setCreateFormExpanded(!createFormExpanded)}
          >
            <span className="text-lg font-semibold">{t('create_account') || 'Create Ad Account'}</span>
            <ChevronDown
              size={20}
              className={cn("text-muted-foreground transition-transform", createFormExpanded && "rotate-180")}
            />
          </button>
        ) : (
          // Static header when no accounts (always expanded)
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('create_account') || 'Create Ad Account'}</CardTitle>
          </CardHeader>
        )}
        {(accounts.length === 0 || createFormExpanded) && (
          <CardContent className={accounts.length > 0 ? "pt-0" : ""}>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Name */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('company_title') || 'Company Name'} *</label>
                <Input
                  type="text"
                  placeholder="Acme Inc."
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Contact Name */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('contact_name') || 'Contact Person'} *</label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('email') || 'Email'} *</label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('phone') || 'Phone'} *</label>
                <Input
                  type="tel"
                  placeholder="+1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Country */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('country') || 'Country'} *</label>
                <Input
                  type="text"
                  placeholder="United States"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  maxLength={128}
                  className="h-11"
                />
              </div>

              {/* City */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('city') || 'City'} *</label>
                <Input
                  type="text"
                  placeholder="New York"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  maxLength={128}
                  className="h-11"
                />
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('address') || 'Address'} *</label>
                <Input
                  type="text"
                  placeholder="123 Main St, Suite 100"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Legal Name (Optional) */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('legal_name') || 'Legal Name (Optional)'}</label>
                <Input
                  type="text"
                  placeholder="Legal entity name"
                  value={formData.legal_name}
                  onChange={(e) => handleInputChange('legal_name', e.target.value)}
                  maxLength={256}
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground">
                  {t('legal_name_hint') || 'Additional legal information displayed to users'}
                </p>
              </div>

              {/* Company About */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('company_about') || 'About Your Company'} *</label>
                <textarea
                  placeholder={t('company_about_placeholder') || 'Describe what your company does...'}
                  value={formData.company_about}
                  onChange={(e) => handleInputChange('company_about', e.target.value)}
                  maxLength={200}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                />
                <p className="text-xs text-muted-foreground flex justify-between">
                  <span>{t('company_about_hint') || '10-200 characters'}</span>
                  <span className={formData.company_about.length < 10 ? 'text-destructive' : ''}>{formData.company_about.length}/200</span>
                </p>
              </div>

              {/* Advertising About */}
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('advertising_about') || 'What Will You Advertise?'} *</label>
                <textarea
                  placeholder={t('advertising_about_placeholder') || 'Describe what you plan to advertise...'}
                  value={formData.advertising_about}
                  onChange={(e) => handleInputChange('advertising_about', e.target.value)}
                  maxLength={200}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                />
                <p className="text-xs text-muted-foreground flex justify-between">
                  <span>{t('advertising_about_hint') || '10-200 characters'}</span>
                  <span className={formData.advertising_about.length < 10 ? 'text-destructive' : ''}>{formData.advertising_about.length}/200</span>
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full shadow-lg shadow-primary/20"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner className="h-4 w-4" />
                    <span>{t('processing') || 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    <span>{t('submit_for_review') || 'Submit for Review'}</span>
                  </>
                )}
              </Button>
            </form>

            <p className="text-xs text-muted-foreground text-center mt-4">
              {t('account_form_note') || 'Your account will be reviewed within 1-2 business days.'}
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export default AccountsPage;
